/**
 * src/components/legal/LegalModal.tsx
 * =============================================================================
 * Modal que muestra UNO de los 3 documentos legales (Términos, Privacidad,
 * Cookies). Single-doc por elección del usuario (2026-05-27): cada link del
 * footer abre solo su documento. Ya no hay tabs para saltar entre los 3
 * sin cerrar el modal — refuerza que cada legal es una pieza independiente
 * y evita que el usuario haga browsing accidental.
 *
 * Patrón visual: mismo backdrop + tarjeta blanca con scroll interno.
 * Esc + click backdrop cierran. Si quieres ver otro documento legal, lo
 * abres desde otro link del footer.
 *
 * Las rutas /legal/* (TermsPage, PrivacyPage, CookiesPage) siguen siendo
 * la versión "página entera" para deep links / SEO.
 * =============================================================================
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, X } from 'lucide-react'
import { TermsContent, PrivacyContent, CookiesContent } from '@/components/legal/LegalContent'
import { useFocusTrap } from '@/hooks/useFocusTrap'

export type LegalDoc = 'terms' | 'privacy' | 'cookies'

interface Props {
  open: boolean
  onClose: () => void
  /** Qué documento mostrar. Cada link del footer pasa el suyo. */
  doc: LegalDoc
}

const LAST_UPDATED = '22 de octubre de 2026'

export function LegalModal({ open, onClose, doc }: Props) {
  const { t } = useTranslation()
  /** Trampa de foco: Tab cicla dentro y al cerrar vuelve al disparador. */
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, open)

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const labelKey =
    doc === 'terms'
      ? 'common:legal.terms'
      : doc === 'privacy'
        ? 'common:legal.privacy'
        : 'common:legal.cookies'

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm px-4 py-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-full bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden"
      >
        {/* Cabecera: título del doc + X (sin tabs). */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-100 shrink-0">
          <h2
            id="legal-modal-title"
            className="text-sm font-semibold text-zinc-900 truncate"
          >
            {t(labelKey)}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="h-7 w-7 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-600 shrink-0"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        {/* Cuerpo scrolleable. */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-3 mb-5">
            <p className="text-xs text-zinc-500 m-0">
              {t('common:legal.lastUpdated', { date: LAST_UPDATED })}
            </p>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2 text-[11px] text-amber-900">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{t('common:legal.draftWarning')}</span>
            </div>
          </div>

          {/* Estilos prose ligeros — mismo aspecto que la versión página completa. */}
          <div className="leading-relaxed text-sm text-zinc-700 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-zinc-900 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:text-zinc-900 [&_p]:my-2.5 [&_ul]:my-2.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:leading-relaxed [&_a]:text-zinc-900 [&_a]:underline [&_a]:underline-offset-2 [&_strong]:font-semibold [&_strong]:text-zinc-900">
            {doc === 'terms' && <TermsContent />}
            {doc === 'privacy' && <PrivacyContent />}
            {doc === 'cookies' && <CookiesContent />}
          </div>
        </div>
      </div>
    </div>
  )
}
